class ReviewsCarousel {
  constructor(
    root,
    wrapper,
    modalsContainer,
    {
      workerUrl,
      shopDomain,
      externalId,
      productId,
      bufferMultiplier = 3,
      nativeApiHost = 'https://api.judge.me',
      useNativeSource = false,
      shopLocale = '',
    }
  ) {
    this.cfg = {
      workerUrl: workerUrl.replace(/\/+$/, ''),
      nativeApiHost: nativeApiHost.replace(/\/+$/, ''),
      shopDomain,
      externalId,
      productId,
      bufferMultiplier,
      // Target language for translating cross-language reviews. Only meaningful
      // for the native source; the proxy already returns text as-is.
      shopLocale: (shopLocale || document.documentElement.lang || '').split('-')[0].toLowerCase(),
    };

    // The native judge.me endpoint includes reviews shared across shops, but it
    // is opt-in. When enabled we try it first and fall back to our proxy worker
    // on any failure, staying on the proxy for consistent pagination. When
    // disabled we use the proxy only, exactly as before this feature existed.
    this.source = useNativeSource ? 'native' : 'proxy';

    this.items = [];
    this.totalCount = 0;
    this.countKnown = false;
    this.cursor = null;
    this.hasMore = true;
    this.apiPage = 0;
    this.inflight = false;
    this.destroyed = false;

    this.wrapper = wrapper;

    this.data = this.wrapper.dataset;

    this.modalTitle = this.data.modalTitle || 'Review Details';
    this.closeLabel = this.data.closeLabel || 'Close';

    this.ratingWrapper = document.querySelector('.rating-wrapper');

    this.spv = this.getSPV();
    this.apiPageSize = this.computeApiPageSize();

    this.root = root;
    this.track = document.createElement('div');
    this.track.className = 'rc-track';
    this.root.appendChild(this.track);

    this.modalsContainer = modalsContainer;

    this.prevBtn = this._btn('.rc-btn-prev', () => this.scrollByPage(-1));
    this.nextBtn = this._btn('.rc-btn-next', () => this.scrollByPage(+1));

    this.onScrollThrottled = this.throttle(() => {
      this.updateButtons();
      this.prefetchIfNeeded();
    }, 120);
    this.track.addEventListener('scroll', this.onScrollThrottled, { passive: true });

    window.addEventListener(
      'resize',
      this.throttle(() => this.onResize(), 150),
      { passive: true }
    );

    this.init();
  }

  async init() {
    await this.fetchAndAppendPage(1);
    this.updateButtons();
    this.prefetchIfNeeded();
    this.ratingWrapperInit();
  }

  ratingWrapperInit() {
    if (this.ratingWrapper) {
      this.ratingWrapper.addEventListener('click', () => {
        const rectTop = this.wrapper.getBoundingClientRect().top;
        const scrollTop = window.scrollY || window.pageYOffset;
        const headerOffset = 0;

        const targetY = rectTop + scrollTop - headerOffset;

        window.scrollTo({
          top: targetY,
          behavior: 'smooth',
        });
      });
    }
  }
  params(page) {
    const p = new URLSearchParams({
      shop_domain: this.cfg.shopDomain,
      per_page: String(this.apiPageSize),
    });
    if (this.cfg.productId) p.set('product_id', this.cfg.productId);
    if (this.cfg.externalId) p.set('external_id', this.cfg.externalId);
    if (this.cursor) {
      p.set('cursor', this.cursor);
    } else {
      p.set('page', String(page));
    }
    return p.toString();
  }

  // Always returns the proxy shape: { reviews, count, nextCursor }.
  // Tries the native judge.me endpoint first; on any failure it permanently
  // falls back to our proxy worker.
  async fetchPage(page) {
    if (this.source === 'native') {
      try {
        const data = await this.fetchNativePage(page);
        if (data && Array.isArray(data.reviews) && data.reviews.length) return data;
        // Native reachable but returned nothing usable -> fall back.
        if (this.items.length === 0) throw new Error('native returned no reviews');
        return data || { reviews: [], count: this.totalCount, nextCursor: null };
      } catch (err) {
        console.warn('[judge-me-wrapper] native source failed, falling back to proxy:', err);
        this.source = 'proxy';
        this.resetForFallback();
      }
    }
    return this.fetchProxyPage(page);
  }

  // Larger batches only for the native source (it also fetches translations, so
  // a single round-trip stalls more easily). The proxy keeps its original batch
  // size so our custom solution behaves exactly as before.
  computeApiPageSize() {
    const multiplier =
      this.source === 'native' ? Math.max(this.cfg.bufferMultiplier, 6) : this.cfg.bufferMultiplier;
    return this.spv * multiplier;
  }

  // Reset state so the proxy fetch behaves exactly like the pre-native code:
  // proxy pagination from page 1 and the proxy's original batch size.
  resetForFallback() {
    this.cursor = null;
    this.hasMore = true;
    this.apiPage = 0;
    this.apiPageSize = this.computeApiPageSize();
  }

  async fetchProxyPage(page) {
    const res = await fetch(`${this.cfg.workerUrl}?${this.params(page)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  nativeParams(page) {
    return new URLSearchParams({
      url: this.cfg.shopDomain,
      shop_domain: this.cfg.shopDomain,
      platform: 'shopify',
      product_id: String(this.cfg.externalId || this.cfg.productId || ''),
      page: String(page),
      per_page: String(this.apiPageSize),
    }).toString();
  }

  async fetchNativePage(page) {
    // prefetchIfNeeded() calls fetchAndAppendPage() with no argument and relies
    // on cursor. For the page-based native API, cursor holds the next page number.
    const effectivePage = Number(page) || Number(this.cursor) || this.apiPage + 1;
    const url = `${this.cfg.nativeApiHost}/reviews/reviews_for_widget?${this.nativeParams(effectivePage)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`native HTTP ${res.status}`);
    const data = await res.json();
    if (!data || typeof data.html !== 'string') throw new Error('native: missing html');

    const doc = new DOMParser().parseFromString(data.html, 'text/html');
    const nodes = [...doc.querySelectorAll('.jdgm-rev')];
    const reviews = nodes.map((el) => this.parseNativeReview(el));

    // Guard against judge.me silently changing their markup: a 200 with valid
    // JSON can still parse into structurally-present-but-empty reviews if the
    // inner class names moved. Treat that as a failure so we fall back instead
    // of rendering blank cards. We only assert this when the API actually
    // claims this page has reviews.
    if (nodes.length && !this.looksLikeValidReviews(reviews)) {
      throw new Error('native: parsed reviews look empty (markup likely changed)');
    }

    // Merge translations (single batch request for the whole page) so cards and
    // the modal can show text in the shop's language. Best-effort: if it fails,
    // reviews simply stay in their original language.
    await this.applyTranslations(reviews);

    const count = Number.isFinite(+data.total_count) ? +data.total_count : this.totalCount;
    // Page-based pagination: there is a next page while we have not yet
    // consumed every review the API reports.
    const consumed = this.items.length + reviews.length;
    const hasMore = reviews.length > 0 && consumed < count;
    return { reviews, count, nextCursor: hasMore ? String(effectivePage + 1) : null };
  }

  // Fetch translations for any reviews not already in the shop language and make
  // the translation the displayed text (the original is kept for the toggle).
  async applyTranslations(reviews) {
    const locale = this.cfg.shopLocale;
    if (!locale) return;

    const needing = reviews.filter((r) => r.language && r.language !== locale);
    const uuids = needing.map((r) => r.id);
    if (!uuids.length) return;

    let map;
    try {
      const p = new URLSearchParams({
        review_uuids: uuids.join(','),
        shop_domain: this.cfg.shopDomain,
        language: locale,
      });
      const res = await fetch(`${this.cfg.nativeApiHost}/api/review_translations?${p.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      map = await res.json();
    } catch (_) {
      return; // best-effort: leave originals in place
    }
    if (!map || typeof map !== 'object') return;

    for (const r of needing) {
      const t = map[r.id];
      const title = t?.title?.success ? t.title.translated_text : null;
      const body = t?.body?.success ? t.body.translated_text : null;
      if (title == null && body == null) continue;
      r.translated = { title: title ?? r.original.title, body: body ?? r.original.body };
      // Default display is the translation.
      r.title = r.translated.title;
      r.body = r.translated.body;
    }
  }

  // Sanity-check that parsing actually extracted meaningful content rather than
  // a list of blank objects (which is what a markup change would produce).
  looksLikeValidReviews(reviews) {
    if (!reviews.length) return false;
    const hasContent = (r) => r.rating > 0 && (r.title || r.body || (r.pictures && r.pictures.length));
    const good = reviews.filter(hasContent).length;
    // Require the majority of reviews on the page to carry real content.
    return good / reviews.length >= 0.6;
  }

  // Parse one judge.me .jdgm-rev element into the same review object shape the
  // proxy returns, so renderSlide / renderModal need no changes.
  parseNativeReview(el) {
    const score = parseFloat(el.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '0');
    const ts = el.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '';
    const pictures = [...el.querySelectorAll('.jdgm-rev__pics .jdgm-rev__pic-link, .jdgm-rev__pics img')].map(
      (node) => {
        const img = node.tagName === 'IMG' ? node : node.querySelector('img');
        const original = node.getAttribute?.('href') || img?.getAttribute('data-src') || img?.src || '';
        const compact = img?.getAttribute('data-src') || img?.src || original;
        return { hidden: false, urls: { compact, original } };
      }
    );

    const title = (el.querySelector('.jdgm-rev__title')?.textContent || '').trim();
    // Plain text to match the proxy shape: the slide escapes body and the modal
    // injects it as HTML, so passing raw markup would render tags.
    const body = (el.querySelector('.jdgm-rev__body')?.textContent || '').trim();

    // reviews_for_widget always returns the original text + its language. The
    // source shop is derivable from the product URL host (reviews can be shared
    // across shops). Translations are fetched separately and merged in later.
    const language = (el.getAttribute('data-review-language') || '').toLowerCase();
    const sourceProductUrl = el.getAttribute('data-product-url') || '';
    let sourceShop = '';
    try {
      sourceShop = sourceProductUrl ? new URL(sourceProductUrl, location.origin).host : '';
    } catch (_) {
      sourceShop = '';
    }

    return {
      id: el.getAttribute('data-review-id') || `${ts}-${Math.random().toString(36).slice(2, 8)}`,
      rating: Number.isFinite(score) ? score : 0,
      title,
      body,
      // Keep the untranslated text so the modal can toggle back to the original.
      original: { title, body },
      translated: null,
      language,
      sourceShop,
      sourceProductUrl,
      reviewer: { name: (el.querySelector('.jdgm-rev__author')?.textContent || '').trim() || 'Anonymous' },
      created_at: this.normalizeNativeDate(ts),
      verified: el.getAttribute('data-verified-buyer') === 'true' ? 'verified-purchase' : '',
      pictures,
    };
  }

  // judge.me timestamps look like "2025-10-02 07:16:16 UTC" — turn that into an
  // ISO string the existing date() formatter can parse.
  normalizeNativeDate(ts) {
    if (!ts) return '';
    const iso = ts.replace(' UTC', 'Z').replace(' ', 'T');
    return Number.isNaN(Date.parse(iso)) ? ts : iso;
  }

  async fetchAndAppendPage(page) {
    if (this.inflight) return;
    this.inflight = true;
    try {
      const data = await this.fetchPage(page);
      const batch = Array.isArray(data.reviews) ? data.reviews : [];
      if (Number.isFinite(+data.count)) {
        this.totalCount = +data.count;
        this.countKnown = true;
      }
      this.cursor = data.nextCursor || null;
      this.hasMore = Boolean(this.cursor);
      if (!batch.length) {
        this.hasMore = false;
        return;
      }

      for (const r of batch) {
        this.track.appendChild(this.renderSlide(r));
        this.modalsContainer.appendChild(this.renderModal(r));
      }
      this.items.push(...batch);
      this.apiPage = page;
    } finally {
      this.inflight = false;
      this.updateButtons();
    }
  }
  getSPV() {
    return matchMedia('(min-width:1024px)').matches ? 3 : matchMedia('(min-width:700px)').matches ? 2 : 1;
  }

  prefetchIfNeeded() {
    if (this.inflight) return;
    if (!this.hasMore) return;

    const remainingPx = this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft;
    // Prefetch earlier for the native source (network + translation round-trip
    // is slower); the proxy keeps its original, tighter threshold.
    const thresholdSlides = this.source === 'native' ? 4 : 1.5;
    const thresholdPx = this.slideWidth() * thresholdSlides;

    if (remainingPx <= thresholdPx) {
      this.fetchAndAppendPage().catch(() => {});
    }
  }

  wordingRating(rating) {
    if (rating == 5) return this.data.fiveStars;
    if (rating == 4) return this.data.fourStars;
    if (rating == 3) return this.data.threeStars;
    if (rating == 2) return this.data.twoStars;
    return this.data.oneStar;
  }

  createHasImagesHtml(images) {
    if (!images || !images.length) return '';

    const hasImages = images.some((img) => img && img.urls && img.urls.compact && !img.hidden);

    const html = `<div class="rc-has-images"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"
     xmlns="http://www.w3.org/2000/svg" aria-hidden="true">

  <rect x="1.5" y="2" width="13" height="12" rx="2"
        stroke="currentColor" stroke-width="1"/>

  <circle cx="11" cy="6" r="1.25" fill="currentColor"/>

  <path d="M3.5 12.5 L6.7 8.8 L8.9 11.4 L10.3 9.7 L12.5 12.5"
        stroke="currentColor" stroke-width="1"
        stroke-linecap="round" stroke-linejoin="round"/>
</svg></div>`;

    return hasImages ? html : '';
  }

  createTitle(r) {
    if (!r.title) return this.wordingRating(r.rating);
    return this.esc(r.title);
  }

  // Whether a review actually carries a translation distinct from its original.
  isTranslated(r) {
    return Boolean(r.translated && (r.translated.title || r.translated.body));
  }

  // Human-readable language name for a code, using the page locale.
  languageName(code) {
    if (!code) return '';
    try {
      const dn = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'language' });
      return dn.of(code) || code;
    } catch (_) {
      return code;
    }
  }

  // Small "Translated from X" hint shown on cards for cross-language reviews.
  createTranslatedHint(r) {
    if (!this.isTranslated(r)) return '';
    const tmpl = this.data.translatedFrom || 'Translated from {{ language }}';
    const label = tmpl.replace('{{ language }}', this.esc(this.languageName(r.language)));
    return `<div class="rc-translated-hint">${label}</div>`;
  }

  renderSlide(r) {
    const el = document.createElement('div');
    el.className = 'rc-slide';
    el.innerHTML = `
        <article class="rc-card modal-link" aria-label="Review" data-modal-id="review-modal-${r.id}">
        <div class="rc-title"><div class="rc-title-text">${this.createTitle(r)}</div> ${this.createHasImagesHtml(
      r.pictures
    )}</div>
          <div class="rc-stars rc-stars--card">${this.stars(r.rating)} ${this.createVerifiedBadge(r)}</div>
          <div class="rc-body">${this.esc(r.body || '')}</div>
          <div class="rc-meta">
            <span>${this.shortReviewerName(r.reviewer.name)}</span>
            <span>${this.date(r.created_at)}</span>
          </div>
          ${this.createTranslatedHint(r)}
        </article>
        `;
    return el;
  }

  createVerifiedBadge(r) {
    if (r.verified !== 'verified-purchase') return '';
    return `<span class="rc-verified-badge" title="${this.data.verifiedPurchase}"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 585.86 585.7"><g><path d="M292.91,0c35.61,0,89.68,76.34,89.68,76.34S474.9,60.5,500.05,85.53C525.23,110.87,509.42,203,509.42,203s76.44,54.17,76.44,89.65c0,35.8-76.44,89.65-76.44,89.65s15.81,92.49-9.37,117.52c-25.15,25.34-117.46,9.5-117.46,9.5s-54.07,76.34-89.68,76.34-89.67-76.34-89.67-76.34S111,525.2,85.78,499.86c-25.15-25-9.38-117.52-9.38-117.52S0,328.49,0,292.69C0,257.21,76.4,203,76.4,203S60.63,110.87,85.78,85.53c25.18-25,117.46-9.19,117.46-9.19S257.34,0,292.91,0Z" style="fill: currentcolor; fill-rule: evenodd;"></path><path d="M289.15,296.22l91-91.34a17.36,17.36,0,0,1,24.55,0l24.54,24.66a17.48,17.48,0,0,1,0,24.65L307.88,375.81a7.18,7.18,0,0,1-1.13,1.33l-24.56,24.65a17.38,17.38,0,0,1-24.66,0L233,377.14a6.78,6.78,0,0,1-1.12-1.33l-63.52-63.7a17.29,17.29,0,0,1,0-24.49L192.89,263a17.37,17.37,0,0,1,24.56,0l48.1,48.16h0l.1.16,4.23,4.3,0-.16,49.17,48.81Z" style="fill: currentcolor; fill-rule: evenodd;"></path></g></svg></span>`;
  }

  shortReviewerName(name) {
    if (!name) return 'Anonymous';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  }

  createImagesHtml(images) {
    if (!images || !images.length) return '';

    const container = document.createElement('div');
    container.className = 'judge-me-wrapper__review-images';
    container.setAttribute('role', 'list');
    const imagesHtml = images
      .map((img) => {
        if (!img || !img.urls || !img.urls.compact || img.hidden || !img.urls.original) return '';

        const src = img.urls.compact;
        const large = img.urls.original;
        return `<img class="judge-me-wrapper__review-image lightbox-img" data-src="${this.esc(large)}" src="${this.esc(
          src
        )}" alt="image">`;
      })
      .join('');

    container.innerHTML = imagesHtml;
    return container.outerHTML;
  }

  renderModal(r) {
    const el = document.createElement('div');
    el.className = 'modal-window-overlay';
    el.id = `review-modal-${r.id}`;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', `review-modal-title-${r.id}`);
    el.setAttribute('aria-describedby', `review-modal-desc-${r.id}`);
    el.setAttribute('tabindex', '-1');

    let titleHtml = '';

    if (r.title) {
      titleHtml = `<div class="judge-me-wrapper__review-detail-title" data-review-title>${this.esc(r.title)}</div>`;
    }

    const html = `
          <div class="modal-window">
            <div class="modal-window-header">
              <h1 class="modal-window-title" id="review-modal-title-${r.id}">${this.modalTitle}</h1>
              <button
                class="modal-close-button"
                aria-label="${this.closeLabel}"
                onclick="window.modalHandler.closeCurrentModal();"
              >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                class="icon icon-close"
                >
                <path d="M18.7071 6.70711C19.0976 6.31658 19.0976 5.68342 18.7071 5.29289C18.3166 4.90237 17.6834 4.90237 17.2929 5.29289L12 10.5858L6.70711 5.29289C6.31658 4.90237 5.68342 4.90237 5.29289 5.29289C4.90237 5.68342 4.90237 6.31658 5.29289 6.70711L10.5858 12L5.29289 17.2929C4.90237 17.6834 4.90237 18.3166 5.29289 18.7071C5.68342 19.0976 6.31658 19.0976 6.70711 18.7071L12 13.4142L17.2929 18.7071C17.6834 19.0976 18.3166 19.0976 18.7071 18.7071C19.0976 18.3166 19.0976 17.6834 18.7071 17.2929L13.4142 12L18.7071 6.70711Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <div class="modal-window-content" id="review-modal-desc-${r.id}}">
            <div class="judge-me-wrapper__review-detail">
            <div class="judge-me-wrapper__review-detail-header">
                <div class="judge-me-wrapper__review-detail-author">${this.shortReviewerName(r.reviewer.name)}</div>
                <div class="judge-me-wrapper__review-detail-date">${this.date(r.created_at)}</div>
                 <div class="judge-me-wrapper__review-detail-rating">${this.stars(r.rating)}</div>
            </div>
            ${this.createSourceHtml(r)}
            ${titleHtml}
            <div class="judge-me-wrapper__review-detail-body" data-review-body>
                      ${r.body}
            </div>
            ${this.createTranslateToggleHtml(r)}

            ${this.createImagesHtml(r.pictures)}

            </div>

            </div>
          </div>
        `;

    el.innerHTML = html;
    this.bindTranslateToggle(el, r);
    return el;
  }

  // "Originally on <shop>" line, shown only when the review came from a
  // different shop than the current one (cross-shop shared reviews).
  createSourceHtml(r) {
    if (!r.sourceShop) return '';
    const currentHost = (this.cfg.shopDomain || '').replace(/^www\./, '');
    const sourceHost = r.sourceShop.replace(/^www\./, '');
    // Compare loosely: myshopify domain vs. public domain won't match, so we
    // only suppress it when hosts are clearly the same storefront.
    if (sourceHost === currentHost) return '';
    const tmpl = this.data.originallyOn || 'Originally on {{ shop }}';
    const label = tmpl.replace('{{ shop }}', this.esc(r.sourceShop.replace(/^www\./, '')));
    const href = r.sourceProductUrl ? this.esc(r.sourceProductUrl) : '';
    const inner = href
      ? `<a href="${href}" target="_blank" rel="noopener nofollow">${label}</a>`
      : label;
    return `<div class="judge-me-wrapper__review-detail-source">${inner}</div>`;
  }

  // Toggle button to switch the modal body/title between translation and original.
  createTranslateToggleHtml(r) {
    if (!this.isTranslated(r)) return '';
    const showOriginal = (this.data.showOriginal || 'Show original ({{ language }})').replace(
      '{{ language }}',
      this.esc(this.languageName(r.language))
    );
    return `<button type="button" class="judge-me-wrapper__translate-toggle" data-translate-toggle data-showing="translated">${showOriginal}</button>`;
  }

  bindTranslateToggle(el, r) {
    const btn = el.querySelector('[data-translate-toggle]');
    if (!btn) return;
    const titleEl = el.querySelector('[data-review-title]');
    const bodyEl = el.querySelector('[data-review-body]');

    const showOriginal = (this.data.showOriginal || 'Show original ({{ language }})').replace(
      '{{ language }}',
      this.languageName(r.language)
    );
    const showTranslation = this.data.showTranslation || 'Show translation';

    btn.addEventListener('click', () => {
      const showingOriginal = btn.dataset.showing === 'original';
      const next = showingOriginal ? r.translated : r.original;
      if (titleEl && next.title) titleEl.textContent = next.title;
      if (bodyEl) bodyEl.textContent = next.body || '';
      btn.dataset.showing = showingOriginal ? 'translated' : 'original';
      btn.textContent = showingOriginal ? showOriginal : showTranslation;
    });
  }

  slideWidth() {
    const firstSlide = this.track.querySelector('.rc-slide');
    return firstSlide ? firstSlide.getBoundingClientRect().width + 12 : this.track.clientWidth;
  }

  scrollByPage(dir) {
    const delta = dir * (this.slideWidth() * this.spv);
    this.track.scrollBy({ left: delta, behavior: 'smooth' });
    this.onScrollThrottled();
  }

  updateButtons() {
    const atStart = this.track.scrollLeft <= 1;
    const atEndPx = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    const fetchedAll = !this.hasMore;

    this.prevBtn.disabled = atStart;
    this.nextBtn.disabled = fetchedAll && atEndPx;
  }

  onResize() {
    const newSpv = this.getSPV();
    if (newSpv === this.spv) return;
    this.spv = newSpv;
    this.updateButtons();
    this.prefetchIfNeeded();
  }

  throttle(fn, ms = 120) {
    let last = 0,
      timer = null;
    return (...args) => {
      const now = Date.now();
      const remain = ms - (now - last);
      if (remain <= 0) {
        last = now;
        fn(...args);
      } else {
        clearTimeout(timer);
        timer = setTimeout(() => {
          last = Date.now();
          fn(...args);
        }, remain);
      }
    };
  }

  stars(n = 0) {
    const full = Math.max(0, Math.min(5, Number(n)));
    const svg = (f) =>
      `<svg viewBox="0 0 20 20" aria-hidden="true"><path fill="${
        f ? 'currentColor' : 'none'
      }" stroke="currentColor" stroke-width="1.5" d="M10 2.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 15.8 4.8 18.6l1-5.8L1.5 8.7l5.9-.9L10 2.5z"/></svg>`;
    return Array.from({ length: 5 }, (_, i) => svg(i < full)).join('');
  }
  esc(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  date(iso, locale = document.documentElement.lang) {
    try {
      return new Date(iso).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }
  _btn(selector, onClick) {
    const b = this.wrapper.querySelector(selector);
    b.addEventListener('click', onClick);
    return b;
  }
}

class RatingStarsHandler {
  constructor({ selector = '.judge-me-wrapper__rating-stars', max = 5, step = 0.25 } = {}) {
    this.selector = selector;
    this.max = max;
    this.step = step;
    this.hosts = [];
    this.init();
  }

  init() {
    this.hosts = [...document.querySelectorAll(this.selector)];
    this.hosts.forEach((el) => this.render(el));

    const mo = new MutationObserver((muts) => {
      for (const m of muts) if (m.attributeName === 'data-score') this.render(m.target);
    });
    this.hosts.forEach((el) => mo.observe(el, { attributes: true, attributeFilter: ['data-score'] }));
  }

  parseScore(el) {
    const raw = (el.getAttribute('data-score') ?? '').replace(',', '.');
    let n = parseFloat(raw);
    if (!Number.isFinite(n)) n = 0;
    return Math.max(0, Math.min(this.max, n));
  }

  quantize(x) {
    const q = Math.round(x / this.step) * this.step;
    return Math.min(1, Math.max(0, +q.toFixed(2)));
  }

  render(el) {
    const score = this.parseScore(el);
    const full = Math.floor(score);
    let part = this.quantize(score - full);
    let fullStars = full;
    if (part >= 1) {
      fullStars += 1;
      part = 0;
    }

    const stars = [];
    for (let i = 0; i < this.max; i++) {
      if (i < fullStars) stars.push(this.star(1));
      else if (i === fullStars && part > 0) stars.push(this.star(part));
      else stars.push(this.star(0));
    }
    el.innerHTML = `<div class="rc-stars" aria-label="${score} out of ${this.max}">${stars.join('')}</div>`;
  }

  star(fillRatio) {
    const pathD = 'M10 2.2l2.7 5.4 6 .9-4.4 4.3 1 6L10 16.3 4.7 18.8l1-6L1.4 8.5l6-.9L10 2.2z';

    if (fillRatio <= 0) {
      return `<svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="${pathD}" style="fill: var(--star-empty, #d1d5db)"></path>
        </svg>`;
    }
    if (fillRatio >= 1) {
      return `<svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="${pathD}" fill="currentColor"></path>
        </svg>`;
    }

    const edge = Math.round(fillRatio * 4) / 4;
    const a = (edge * 100).toFixed(2);
    const b = Math.min(100, edge * 100 + 0.01).toFixed(2);
    const gid = `grad-${Math.random().toString(36).slice(2, 8)}`;

    return `
        <svg viewBox="0 0 20 20" aria-hidden="true">

          <path d="${pathD}" style="fill: var(--star-empty, #d1d5db)"></path>

          <defs>
            <linearGradient id="${gid}" x1="0" x2="1">
              <stop offset="${a}%" stop-color="currentColor"></stop>
              <stop offset="${b}%" stop-color="transparent"></stop>
            </linearGradient>
          </defs>
          <path d="${pathD}" fill="url(#${gid})"></path>
        </svg>`;
  }
}

class WriteReviewModalHandler {
  constructor() {
    this.init();
  }

  init() {
    const writeEl = document.querySelector('.judge-me-wrapper__write-review');
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && node.classList.contains('jdgm-write-review-modal')) {
            writeEl.classList.add('loaded');
            observer.disconnect();
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  open() {
    const modal = document.querySelector('.jdgm-write-review-modal');
    const introSection = document.querySelector('.jdgm-write-review-modal__section--review-form-intro');
    const introPage = document.querySelector('.jdgm-write-review-modal__page--review-form-intro');

    if (modal) {
      introSection.classList.add('current-section');
      introPage.classList.add('current-page');
      modal.style.display = 'block';
    }
  }
}

// Judge.me renders its review image lightbox (a magnific-popup fork, classes
// jm-mfp-*) directly in <body> with z-index ~1043. Our review modal uses the
// max 32-bit z-index (2147483647), so the lightbox opens underneath it and only
// the part not covered by the modal is visible.
//
// We can't out-number the modal (the browser clamps z-index to that same max),
// so we do two things together: raise the lightbox to the *same* max z-index
// AND move it to the end of <body>. Both nodes then resolve in the root
// stacking context at equal z-index, where the later DOM node wins -> the
// lightbox renders on top. Class-based, so it survives judge.me re-rendering.
class JudgemeLightboxZFix {
  // Max 32-bit signed int — the browser clamps z-index to this, and it matches
  // our modal overlays, so DOM order becomes the tie-breaker.
  static Z = 2147483647;

  constructor() {
    this.raise = this.raise.bind(this);
    // Fix anything already present, then watch for future openings.
    this.raise();
    this.observer = new MutationObserver(this.raise);
    this.observer.observe(document.body, { childList: true });
  }

  raise() {
    const wrap = document.querySelector('.jm-mfp-wrap');
    if (!wrap) return;

    const bg = document.querySelector('.jm-mfp-bg');
    if (bg) bg.style.zIndex = String(JudgemeLightboxZFix.Z);
    wrap.style.zIndex = String(JudgemeLightboxZFix.Z);

    // Nothing to do if the lightbox already sits after every open modal in the
    // DOM. Bail early to avoid an append loop with our own MutationObserver.
    const openModals = [...document.querySelectorAll('.modal-window-overlay.show')];
    const needsRaise = openModals.some(
      (m) => wrap.compareDocumentPosition(m) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    if (!needsRaise) return;

    // Append the dimming background first, then the wrap, so the image wrap
    // ends up last (above both the background and any open modal).
    if (bg) document.body.appendChild(bg);
    document.body.appendChild(wrap);
  }
}

// Judge.me lazy-loads review images (real URL in data-src, kept hidden via the
// jdgm--loading class on the picture link until its own lazy-loader swaps in
// src). Inside our modal that loader never fires, so the images stay at
// visibility:hidden / height:1px. When a review modal opens, eagerly promote
// data-src -> src and drop the loading class so the images actually show.
class JudgemeModalLazyImages {
  constructor() {
    this.reveal = this.reveal.bind(this);

    // Reveal on any modal open (our modal handler toggles the .show class).
    document.addEventListener('click', (e) => {
      if (e.target.closest('.modal-link')) {
        // Let the modal open first, then reveal its images.
        requestAnimationFrame(() => this.reveal());
      }
    });

    // Also cover reviews paginated/sorted in after the modal is already open.
    this.observer = new MutationObserver(() => this.reveal());
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  reveal(root = document) {
    root.querySelectorAll('.jdgm-rev__pic-img[data-src]').forEach((img) => {
      const src = img.getAttribute('data-src');
      if (src && !img.getAttribute('src')) img.setAttribute('src', src);
      const link = img.closest('.jdgm-rev__pic-link');
      if (link) link.classList.remove('jdgm--loading');
    });
  }
}
